import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * rt_ — agency-wide realtime event subscription.
 *
 * Long-poll `/events/poll` every 30s (cPanel-safe, firewall-friendly)
 * and, for each event, invalidate the TanStack Query keys that depend
 * on the affected data. This hook is intentionally keyed off an event
 * type enum so pages only need to import the hook once.
 *
 * Contract with the backend (`App\Http\Controllers\Api\RealtimeController`):
 *
 *   GET /events/poll?since={cursor}
 *   → { data: Event[], cursor: number, now: number }
 *
 *   Event = {
 *     id: number        // monotonic ms epoch — used as the next cursor
 *     type: string      // e.g. "mutation_executed" | "finding_created"
 *     payload: object
 *     timestamp: string // ISO8601
 *   }
 */

export interface RealtimeEvent {
  id: number;
  type: string;
  payload: Record<string, unknown>;
  timestamp: string;
}

interface Options {
  /** Override the poll interval in ms (default 30_000). */
  intervalMs?: number;
  /** Optional per-type callback invoked after cache invalidation. */
  onEvent?: (event: RealtimeEvent) => void;
}

export function useRealtimeEvents({
  intervalMs = 30_000,
  onEvent,
}: Options = {}) {
  const queryClient = useQueryClient();
  const cursorRef = useRef<number>(0);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    let cancelled = false;

    async function tick() {
      try {
        const resp = await api.get<{
          data: RealtimeEvent[];
          cursor: number;
        }>('/events/poll', { params: { since: cursorRef.current } });

        if (cancelled) return;

        cursorRef.current = resp.data.cursor ?? cursorRef.current;

        for (const evt of resp.data.data ?? []) {
          switch (evt.type) {
            case 'mutation_executed':
              queryClient.invalidateQueries({ queryKey: ['mutations'] });
              queryClient.invalidateQueries({ queryKey: ['accounts'] });
              break;
            case 'finding_created':
              queryClient.invalidateQueries({ queryKey: ['findings'] });
              queryClient.invalidateQueries({ queryKey: ['alerts'] });
              break;
            case 'sync_completed':
              queryClient.invalidateQueries({ queryKey: ['accounts'] });
              break;
            default:
              // unknown event — ignore silently
              break;
          }
          onEventRef.current?.(evt);
        }
      } catch {
        // Long-poll failures are benign — we retry next interval.
      }
    }

    // Fire once immediately, then on an interval.
    tick();
    const handle = window.setInterval(tick, intervalMs);

    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [intervalMs, queryClient]);
}
